import {
  SALES_INSIGHTS_ACTIVITY_GROUPS,
  SalesInsightsActivityGroup,
  SalesInsightsActivityRow
} from '../models/sales-insights.model';

export type SalesInsightsCalendarView = 'month' | 'week';

export interface SalesInsightsActivitySection {
  group: SalesInsightsActivityGroup;
  labelKey: string;
  count: number;
  rows: SalesInsightsActivityRow[];
}

export interface SalesInsightsMovedDates {
  activityDate: string;
  activityEndDate: string | null;
}

const PALETTE_SIZE = 8;
const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const OFFSET_WITHOUT_COLON = /([+-]\d{2})(\d{2})$/;

export function salesInsightsDayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Date-only values are local days; a timestamp with "+0000" (the Java default) is not parsed by every browser.
export function salesInsightsParseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const day = DAY_ONLY.exec(value);
  if (day) {
    return new Date(+day[1], +day[2] - 1, +day[3]);
  }
  const date = new Date(value.replace(OFFSET_WITHOUT_COLON, '$1:$2'));
  return isNaN(date.getTime()) ? null : date;
}

export function salesInsightsActivityDay(value: string | null | undefined): string | null {
  const date = salesInsightsParseDate(value);
  return date ? salesInsightsDayKey(date) : null;
}

export function salesInsightsActivityGroup(
  row: Pick<SalesInsightsActivityRow, 'done' | 'activityDate'>,
  today: string
): SalesInsightsActivityGroup {
  if (row.done) {
    return 'done';
  }
  const day = salesInsightsActivityDay(row.activityDate);
  if (!day || day > today) {
    return 'planned';
  }
  return day < today ? 'overdue' : 'today';
}

// Done activities are listed newest first, the rest in the order they are due.
export function salesInsightsActivitySections(
  rows: SalesInsightsActivityRow[],
  groupCounts: Partial<Record<SalesInsightsActivityGroup, number>> | null | undefined,
  today: string,
  groups: SalesInsightsActivityGroup[] = SALES_INSIGHTS_ACTIVITY_GROUPS
): SalesInsightsActivitySection[] {
  const time = (row: SalesInsightsActivityRow) => salesInsightsParseDate(row.activityDate)?.getTime() ?? 0;
  return SALES_INSIGHTS_ACTIVITY_GROUPS.filter(group => groups.includes(group))
    .map(group => {
      const sectionRows = rows
        .filter(row => salesInsightsActivityGroup(row, today) === group)
        .sort((a, b) => (group === 'done' ? time(b) - time(a) : time(a) - time(b)));
      return {
        group,
        labelKey: `SALES_INSIGHTS_ACTIVITY_GROUP_${group.toUpperCase()}`,
        count: groupCounts?.[group] ?? sectionRows.length,
        rows: sectionRows
      };
    })
    .filter(section => section.count > 0 || section.rows.length > 0);
}

// Keeps the time of day and the duration, so a 13:00 to 14:00 visit dropped on another day is still 13:00 to 14:00.
export function salesInsightsMovedActivityDates(
  activityDate: string,
  activityEndDate: string | null | undefined,
  targetDay: string
): SalesInsightsMovedDates | null {
  const start = salesInsightsParseDate(activityDate);
  const target = DAY_ONLY.exec(targetDay);
  if (!start || !target) {
    return null;
  }
  const moved = new Date(start);
  moved.setFullYear(+target[1], +target[2] - 1, +target[3]);
  const end = salesInsightsParseDate(activityEndDate);
  return {
    activityDate: moved.toISOString(),
    activityEndDate: end ? new Date(end.getTime() + moved.getTime() - start.getTime()).toISOString() : null
  };
}

export function salesInsightsStartOfWeek(date: Date): Date {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
}

export function salesInsightsCalendarDays(view: SalesInsightsCalendarView, cursor: Date): Date[] {
  const first =
    view === 'week'
      ? salesInsightsStartOfWeek(cursor)
      : salesInsightsStartOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const last =
    view === 'week'
      ? addDays(first, 6)
      : addDays(salesInsightsStartOfWeek(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)), 6);
  const days: Date[] = [];
  for (let day = first; day <= last; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

export function salesInsightsShiftCursor(view: SalesInsightsCalendarView, cursor: Date, step: number): Date {
  return view === 'week' ? addDays(cursor, step * 7) : new Date(cursor.getFullYear(), cursor.getMonth() + step, 1);
}

export function salesInsightsIsoWeek(date: Date): number {
  const thursday = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  thursday.setUTCDate(thursday.getUTCDate() + 4 - (thursday.getUTCDay() || 7));
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return Math.ceil(((thursday.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

// Types keep their colour across list and calendar: the position in the type table picks the palette entry.
export function salesInsightsTypeColorIndex(
  type: string | null | undefined,
  typeIds: string[],
  overrides?: { [type: string]: number } | null
): number {
  if (type && overrides?.[type]) {
    return overrides[type];
  }
  const position = type ? typeIds.indexOf(type) : -1;
  const index = position >= 0 ? position : hash(type || '');
  return (index % PALETTE_SIZE) + 1;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function hash(value: string): number {
  let result = 0;
  for (let i = 0; i < value.length; i++) {
    result = (result * 31 + value.charCodeAt(i)) >>> 0;
  }
  return result;
}

// The create modal closes with the saved activity (or the saved list for several customers) as JSON, and with a plain
// string when it is cancelled. Null means nothing was saved.
export function salesInsightsSavedActivityIds(result: unknown): string[] | null {
  if (typeof result !== 'string' || !/^\s*[[{]/.test(result)) {
    return null;
  }
  try {
    const parsed = JSON.parse(result);
    const activities = Array.isArray(parsed) ? parsed : [parsed];
    return activities.map(activity => activity?.id).filter((id): id is string => typeof id === 'string' && !!id);
  } catch {
    return null;
  }
}

export type SalesInsightsDueKind = 'done' | 'overdue' | 'today' | 'tomorrow' | 'soon' | 'later';

export interface SalesInsightsDue {
  kind: SalesInsightsDueKind;
  days: number;
}

const SOON_DAYS = 6;

// Days are counted between local calendar days, so 23:30 yesterday is one day ago and not zero.
export function salesInsightsDue(day: string | null | undefined, today: string, done = false): SalesInsightsDue | null {
  const target = day ? DAY_ONLY.exec(day) : null;
  const current = DAY_ONLY.exec(today);
  if (!target || !current) {
    return null;
  }
  const days = Math.round(
    (Date.UTC(+target[1], +target[2] - 1, +target[3]) - Date.UTC(+current[1], +current[2] - 1, +current[3])) /
      86_400_000
  );
  if (done) {
    return { kind: 'done', days };
  }
  if (days < 0) {
    return { kind: 'overdue', days };
  }
  if (days === 0) {
    return { kind: 'today', days };
  }
  if (days === 1) {
    return { kind: 'tomorrow', days };
  }
  return { kind: days <= SOON_DAYS ? 'soon' : 'later', days };
}

// Metadata names are often stored in capitals ("PHONE", "NEW"); those read as words. Short codes such as "CRM" and
// names already written in mixed case are kept.
export function salesInsightsReadableName(name: string | null | undefined): string {
  const text = String(name ?? '')
    .replace(/_/g, ' ')
    .trim();
  const letters = text.replace(/[^\p{L}]/gu, '');
  if (letters.length <= 3 || text !== text.toUpperCase()) {
    return text;
  }
  const lower = text.toLocaleLowerCase('sv');
  return lower.charAt(0).toLocaleUpperCase('sv') + lower.slice(1);
}

export function salesInsightsInitials(name: string | null | undefined): string {
  const words = String(name ?? '')
    .trim()
    .split(/[\s._-]+/)
    .filter(word => !!word);
  if (!words.length) {
    return '';
  }
  if (words.length === 1) {
    const word = words[0];
    return word.length <= 3 && word === word.toUpperCase() ? word : word.charAt(0).toLocaleUpperCase('sv');
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toLocaleUpperCase('sv');
}

const TYPE_ICON_RULES: [RegExp, string][] = [
  [/phone|tele|call|samtal|(^|\s)ring/, 'fa-phone'],
  [/mail|mejl|e-post|epost|letter|brev/, 'fa-envelope'],
  [/meet|möte|mote|teams|video|zoom/, 'fa-users'],
  [/visit|besök|besok|store|butik|field/, 'fa-location-dot'],
  [/demo|present|utbild|training/, 'fa-person-chalkboard'],
  [/order|offert|quote/, 'fa-file-signature'],
  [/task|todo|to do|uppgift|att göra/, 'fa-list-check'],
  [/note|anteckn|memo/, 'fa-note-sticky'],
  [/fair|mässa|massa|event/, 'fa-flag']
];
const DEFAULT_TYPE_ICON = 'fa-calendar-check';
const ICON_STYLE = /\b(fa-solid|fa-regular|fa-light|fa-thin|fa-duotone|fa-brands|fas|far|fal|fab)\b/;

// An icon configured for the type code wins; otherwise the type's name decides, in English or Swedish.
export function salesInsightsTypeIcon(
  type: string | null | undefined,
  name: string | null | undefined,
  overrides?: { [type: string]: string } | null
): string {
  const configured = type ? overrides?.[type] : null;
  if (configured) {
    return ICON_STYLE.test(configured) ? configured : `fa-solid ${configured}`;
  }
  const text = `${name || ''} ${type || ''}`.toLocaleLowerCase('sv');
  const rule = TYPE_ICON_RULES.find(([pattern]) => pattern.test(text));
  return `fa-solid ${rule ? rule[1] : DEFAULT_TYPE_ICON}`;
}

export interface SalesInsightsVisitCandidate {
  customerId: string;
  visitRequired: boolean;
  daysSinceLastContact: number | null;
  daysSinceLastVisit?: number | null;
}

// Only activities of the types that count as visits count; a backend without visit days falls back to any contact.
export function salesInsightsDaysSinceVisit(row: SalesInsightsVisitCandidate): number | null {
  const days = row.daysSinceLastVisit === undefined ? row.daysSinceLastContact : row.daysSinceLastVisit;
  return days === undefined ? null : days;
}

// Customers that need visits and have had no visit for the given number of days, the ones never visited first and
// then the longest since.
export function salesInsightsVisitSuggestions<T extends SalesInsightsVisitCandidate>(rows: T[], days: number): T[] {
  const since = (row: T) => salesInsightsDaysSinceVisit(row) ?? Infinity;
  return rows.filter(row => row.visitRequired && since(row) >= days).sort((a, b) => since(b) - since(a));
}
